import React, { useEffect, useMemo, useRef, useState } from 'react';

import style from './index.module.scss';
import moment, { Moment } from 'moment';
import { IScheduleList } from 'type/meeting';
import { CurrentMode, CurrentStatus, TimeStatus } from 'page/MeetingSchedule/types/type';
import classnames from 'classnames';
import { useScheduleContext } from 'page/MeetingSchedule/Provider/ScheduleProvider';
import MeetingDetailDialog from '../MeetingDetailDialog';
import MoreMeetingDialog from '../MoreMeetingDialog';
import { detailModalCaculate, moreModalCaculate } from 'page/MeetingSchedule/utils/modalPosition';
import { useBlinkingBackground } from '../hook/useBlinkingBackground';
import { useHistory } from 'react-router-dom';
import { queryStringify } from 'util/format';
import { getLatestTime } from 'page/MeetingSchedule/utils/timeUtils';
import { useDispatch } from 'react-redux';
import { setMeetingOrderParams } from 'model/meeting';

interface IProps {
  itemTime: Moment;
  meetingList: IScheduleList[];
  index: number;
  maxNumberPayload: number;
  binkTime: Moment | undefined;
}

const nowTime = moment(moment());
const CalendarItem: React.FC<IProps> = (props) => {
  const dispatch = useDispatch();
  const { itemTime, index, maxNumberPayload, meetingList, binkTime } = props;
  const { scheduleDispatch, originState } = useScheduleContext();
  const { currentMode, isFirstBlink } = originState;
  const [visibleMeetingList, setVisibleMeetingList] = useState(meetingList);
  const [addMeetingItemVisiable, setAddMeetingItemVisiable] = useState(false);
  const [isShowMeetingDetail, setIsShowMeetingDetail] = useState(false);
  const [isShowMoreMeeting, setIsShowMoreMeeting] = useState(false);
  const [detailModalPosition, setDetailModalPostion] = useState({ left: 0, top: 0 });
  const [moreModalPosition, setMoreModalPosition] = useState({ left: 0, top: 0 });
  const [currentActiveItem, setCurrentActiveItem] = useState<CurrentStatus | number>(-1);
  const { backgroundColor, startBlinking } = useBlinkingBackground('white', '#68B2FF');
  const history = useHistory();

  useEffect(() => {
    if (isFirstBlink && binkTime && itemTime.isSame(binkTime, 'day')) {
      startBlinking();
      scheduleDispatch({
        type: 'SET_STATE',
        payload: {
          isFirstBlink: false
        }
      });
    }
  }, [binkTime]);

  useEffect(() => {
    setVisibleMeetingList((item) => {
      if (meetingList.length < maxNumberPayload) {
        return meetingList;
      } else {
        // 多于时应该还有一个「更多」
        return meetingList.slice(0, maxNumberPayload - 1);
      }
    });
  }, [maxNumberPayload, meetingList]);

  // 非当前月份、当前月份、今天
  const dataStatus = useMemo(() => {
    const isToday = nowTime.isSame(itemTime, 'day');
    const isCurrentMonth = nowTime.isSame(itemTime, 'month');
    if (isToday) {
      return TimeStatus.today;
    } else if (isCurrentMonth) {
      return TimeStatus.currentMonth;
    } else {
      return TimeStatus.notCurrentMonth;
    }
  }, [itemTime]);

  const timeText = useMemo(() => {
    const isFirstDayOfMonth = itemTime.startOf('month').isSame(itemTime, 'day');
    const isFirstOfList = index === 0;
    if (isFirstOfList || isFirstDayOfMonth) {
      return itemTime.format('MM月DD号');
    } else {
      return itemTime.format('DD');
    }
  }, [itemTime]);

  const currentSchedule = useMemo(() => {
    if (currentActiveItem !== CurrentStatus.end && currentActiveItem > -1) {
      return visibleMeetingList[currentActiveItem];
    }
    return {};
  }, [currentActiveItem, visibleMeetingList]);

  const goToScheduleView = (e: any) => {
    e.stopPropagation();
    scheduleDispatch({
      type: 'SET_STATE',
      payload: {
        currentTime: itemTime.valueOf(),
        currentMode: CurrentMode.schedule,
        isScheduleDateSelected: true
      }
    });
  };

  const handleAddMeeting = (e: any) => {
    e.stopPropagation();
    const now = moment(moment());
    if (itemTime.isBefore(now, 'day')) return;
    setAddMeetingItemVisiable(true);
    setTimeout(() => {
      const query: any = { from: 'schedule' };
      query['currentMode'] = currentMode;
      let timeTarget = itemTime;
      if (meetingList.length > 0) {
        let lastTime = moment(meetingList[meetingList.length - 1].endTime);
        timeTarget = lastTime.isBefore(now) ? now : lastTime;
      }
      const startTime = getLatestTime(timeTarget);
      query['startTime'] = startTime.valueOf();
      query['endTime'] = startTime.add(30, 'minutes').valueOf();
      dispatch(setMeetingOrderParams({ startTime: startTime.valueOf(), endTime: startTime.add(30, 'minutes').valueOf() }));
      setAddMeetingItemVisiable(false);
      history.push(`/order-meeting${queryStringify(query) || ''}`);
    }, 200);
  };

  const handleOpenDetail = (e: any, index: number) => {
    e.stopPropagation();
    const target = e.currentTarget;
    const rect = target.getBoundingClientRect();
    const { top, left } = detailModalCaculate(rect);
    setDetailModalPostion({ top, left });
    setIsShowMeetingDetail(true);
    setCurrentActiveItem(index);
  };
  const handleOpenMoreMeeting = (e: any) => {
    e.stopPropagation();
    const target = e.currentTarget;
    const rect = target.getBoundingClientRect();
    // 计算更多弹窗当前所占用的高度
    const { top, left } = moreModalCaculate(rect, meetingList.length);
    setMoreModalPosition({ top, left });
    setIsShowMoreMeeting(true);
    setCurrentActiveItem(CurrentStatus.end);
  };

  const timeClassName = (dataStatus: TimeStatus) => {
    return classnames(style.textItem, {
      [style.today]: dataStatus == TimeStatus.today,
      [style.currentMonth]: dataStatus == TimeStatus.currentMonth,
      [style.notCurrentMonth]: dataStatus == TimeStatus.notCurrentMonth,
      [style.textLong]: timeText.length > 2
    });
  };

  // 子item的样式（选中时的样式）
  const itemAcviveClassName = (index: number) => {
    return classnames(style.meeting_list_item, {
      [style.meeting_list_item_active]: index == currentActiveItem
    });
  };
  const itemAcviveClassNameEnd = () => {
    return classnames(style.meeting_list_item, {
      [style.meeting_list_item_active]: currentActiveItem == CurrentStatus.end
    });
  };

  // 子item的样式(过去和未来时间样式)
  const itemTimeTextClassName = (time: Moment) => {
    return classnames(style.meeting_list_item_text, {
      [style.meeting_list_item_text_future]: time.isAfter(moment()),
      [style.meeting_list_item_text_history]: time.isBefore(moment())
    });
  };

  useEffect(() => {
    const handleResize = () => {
      if (isShowMeetingDetail) {
        setIsShowMeetingDetail(false);
        setCurrentActiveItem(-1);
      }

      if (isShowMoreMeeting) {
        setIsShowMoreMeeting(false);
        setCurrentActiveItem(-1);
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isShowMeetingDetail, isShowMoreMeeting, meetingList]);

  return (
    <>
      <div style={{ backgroundColor }} className={style.warp} onClick={handleAddMeeting}>
        <div onClick={goToScheduleView} className={timeClassName(dataStatus)}>
          {timeText}
        </div>
        <div className={style.meeting_list}>
          {visibleMeetingList.map((item, index) => {
            return (
              <div
                className={itemAcviveClassName(index)}
                onClick={(e) => {
                  handleOpenDetail(e, index);
                }}
              >
                <div className={style.meeting_list_item_icon}></div>
                <div className={itemTimeTextClassName(moment(Number(item.beginTime)))}>
                  {moment(Number(item.beginTime)).format('HH:mm')} {item.subject}
                </div>
              </div>
            );
          })}
          {meetingList.length > visibleMeetingList.length && (
            <div className={itemAcviveClassNameEnd()} onClick={handleOpenMoreMeeting}>
              <div className={style.meeting_list_item_icon}></div>
              <div className={itemTimeTextClassName(itemTime)}>还有{meetingList.length - visibleMeetingList.length}个会议</div>
            </div>
          )}
          {meetingList.length < maxNumberPayload && addMeetingItemVisiable && (
            <div className={style.meeting_list_item_add}>
              <div className={style.meeting_list_item_icon}></div>
              <div className={style.meeting_list_item_text}>新建日程</div>
            </div>
          )}
        </div>
      </div>
      <MeetingDetailDialog
        modalLeft={detailModalPosition.left}
        modalTop={detailModalPosition.top}
        show={isShowMeetingDetail}
        scheduleDetail={currentSchedule as IScheduleList}
        onCancel={() => {
          setIsShowMeetingDetail(false);
          setCurrentActiveItem(-1);
        }}
      ></MeetingDetailDialog>
      <MoreMeetingDialog
        meetingList={meetingList}
        currentTime={itemTime}
        modalLeft={moreModalPosition.left}
        modalTop={moreModalPosition.top}
        show={isShowMoreMeeting}
        onCancel={() => {
          setIsShowMoreMeeting(false);
          setCurrentActiveItem(-1);
        }}
      ></MoreMeetingDialog>
    </>
  );
};
export default CalendarItem;
